import type { UserFeatureId } from "@wallyo/contracts/actions";
import type {
  CustomerFeatureListItem,
  CustomerFeatureValue,
  FeatureSubject,
  ListCustomerFeatureValuesResponse,
  StaffFeatureSubject,
} from "@wallyo/contracts/console/configuration";
import { useEffect, useMemo, useState } from "react";
import { listCustomerFeatureValues, listCustomerFeatures, saveCustomerFeatures } from "../../api/console-api";
import { ConsoleApiError } from "../../api/errors";
import { useApi } from "../../api/context";
import { customerFeatureName, t } from "../../i18n";
import { useHash } from "../../navigation/use-hash";
import { useAlerts } from "../../shell/alerts";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { LoadFailed, Loading, useLoad } from "../config-parts";
import { describeConfigFailure } from "../config-refusal";
import { selectedId } from "../config-route";
import { toLocalInput } from "../local-datetime";
import { Modal, ReasonField, WriteFailureNotice, useIntentWrite } from "../team/TeamDialogs";
import {
  EMPTY_DRAFT,
  changedFeatures,
  cleanUuid,
  emptyFilters,
  isCompleteUuid,
  emptySubject,
  saveProblem,
  sameSubject,
  saveRequest,
  subjectOf,
  visibleFeatures,
  withToggle,
  type Draft,
  type FeatureFilters,
  type SubjectFields,
} from "./feature-save-model";
import { STAFF_FEATURES } from "../team/team-access";
import { listStaffFeatures, saveStaffFeatures } from "../../api/console-api";
import { StaffFeatureTable, StaffSubjectBar } from "./StaffFeatureTable";
import {
  changedStaffFeatures,
  emptyStaffSubject,
  sameStaffSubject,
  saveStaffRequest,
  staffSaveProblem,
  staffSubjectOf,
  withStaffToggle,
  type StaffSubjectFields,
} from "./staff-feature-model";
import {
  FEATURE_STAFF_FEATURES,
  emptyFields,
  exceptionProblem,
  featureLock,
  fieldsFrom,
  removeProblem,
  scopeLabel,
  sendRemoveException,
  sendSetException,
  setRequest,
  targetLabel,
  valueLabel,
  type ExceptionFields,
} from "./features-model";
import {
  FeatureFilterBar,
  FeatureTable,
  SaveBar,
  SubjectBar,
  SurfaceTabs,
  countLabel,
  type FeatureSurface,
} from "./FeatureTableView";
import { ExceptionFormView, FeatureDetail } from "./FeaturesView";
import { PersonPicker } from "./PersonPicker";

/** The screen's own words for each refusal the server can give on a feature. */
export const FEATURE_REFUSALS = {
  critical: "features.refused.critical",
  scope_not_supported: "features.refused.scopeNotSupported",
  scope_not_allowed: "features.refused.scopeNotAllowed",
  expiry_in_past: "features.refused.expiryInPast",
  target_not_found: "features.refused.targetNotFound",
} as const;

export const describeFeatureFailure = (error: unknown) =>
  describeConfigFailure(error, FEATURE_REFUSALS, "features.stale");

type Editor = { kind: "add" } | { kind: "edit"; value: CustomerFeatureValue };

/** How many console features exist, for the tab's count. */
const STAFF_FEATURE_COUNT = STAFF_FEATURES.length;

/**
 * What a UUID that resolved to nobody actually means — an answer, not a
 * broken screen.
 *
 * Staff and customers share one sequence, so a number this tab cannot find
 * may still be a real badge. The server says which, and the reason is where
 * it says it: `not_found` arrives as `validation_error` carrying the type it
 * was thrown with (console-error.ts).
 */
function missingSubject(error: unknown): "none" | "unknown" | "staff" {
  if (!(error instanceof ConsoleApiError)) return "none";
  if (error.reason === "uuid_is_staff") return "staff";
  return error.reason === "no_such_user" ? "unknown" : "none";
}

/**
 * The Walliyo console tab.
 *
 * The app tab's sibling over the staff catalogue: two records — everyone, or
 * one employee by their UUID — a draft held until Save, and one transaction
 * per save. Nothing cascades here, because this writes `allStaff` and
 * `staffMember` values, and neither carries permissions the way a role grant
 * does; role grants stay in Rôles & équipe for exactly that reason.
 */
function ConsoleSurface() {
  const { client } = useApi();
  const [fields, setFields] = useState<StaffSubjectFields>(emptyStaffSubject);
  const [searched, setSearched] = useState<StaffFeatureSubject | null>(null);
  const [searches, setSearches] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [reason, setReason] = useState("");
  const alerts = useAlerts();
  const save = useIntentWrite();

  const key = searched === null ? null : `member:${searched.uuid}#${searches}`;
  const list = useLoad(searched === null ? null : () => listStaffFeatures(client, searched), key);

  const loading = searched !== null && list.state.status === "loading";
  const features = list.state.data?.features ?? [];
  const changed = changedStaffFeatures(features, draft);
  const missing = searched !== null && list.state.status === "failed" ? missingStaffSubject(list.state.error) : "none";
  const typed = staffSubjectOf(fields);
  const stale = searched !== null && !loading && (typed === null || !sameStaffSubject(typed, searched));

  const search = () => {
    // No group to fall back to here: without the whole number there is nobody
    // to show, so the screen says so rather than fetching something else.
    if (typed === null) {
      setProblem(t("features.subject.needsUuid"));
      return;
    }
    setProblem(null);
    setDraft(EMPTY_DRAFT);
    setReason("");
    setSearched(typed);
    setSearches((count) => count + 1);
  };

  const submit = () => {
    if (searched === null) return;
    save.edited();
    void save.run(
      staffSaveProblem(reason, features, draft),
      (idempotencyKey) =>
        saveStaffFeatures(client, saveStaffRequest(searched, reason, features, draft), { idempotencyKey }),
      ({ saved }) => {
        setDraft(EMPTY_DRAFT);
        setReason("");
        alerts.ok(countLabel("done", saved));
        list.reload();
      },
    );
  };

  return (
    <>
      <StaffSubjectBar
        fields={fields}
        resolved={list.state.data?.subject ?? null}
        missing={missing}
        problem={problem}
        searching={loading}
        onChange={(next) => {
          setProblem(null);
          setFields((current) => ({
            ...current,
            ...next,
            ...(next.uuid === undefined ? {} : { uuid: cleanUuid(next.uuid) }),
          }));
        }}
        onSearch={search}
      />
      {searched === null ? (
        <div className="card">
          <div className="empty">{t("features.subject.pickFirst")}</div>
        </div>
      ) : null}
      {searched !== null && list.state.status === "failed" && missing === "none" ? (
        <LoadFailed
          message={t("features.loadFailed")}
          retry={t("features.retry")}
          error={list.state.error}
          onRetry={list.reload}
        />
      ) : null}
      {!list.state.data && loading ? <Loading text={t("features.loading")} /> : null}
      {list.state.data && missing === "none" ? (
        <>
          {stale ? (
            <div className="banner" role="status">
              {t("features.subject.stale")}
            </div>
          ) : null}
          <StaffFeatureTable
            features={features}
            draft={draft}
            onToggle={(feature) => {
              save.edited();
              setDraft((current) => withStaffToggle(current, feature));
            }}
          />
          {save.error ? (
            <WriteFailureNotice failure={describeFeatureFailure(save.error)} onReload={list.reload} />
          ) : null}
          <SaveBar
            count={changed.length}
            reason={reason}
            pending={save.pending}
            problem={save.problem}
            onReason={(next) => {
              save.edited();
              setReason(next);
            }}
            onDiscard={() => {
              setDraft(EMPTY_DRAFT);
              setReason("");
              save.edited();
            }}
            onSave={submit}
          />
        </>
      ) : null}
    </>
  );
}

/** A UUID that names no employee: unknown, or a customer's. */
function missingStaffSubject(error: unknown): "none" | "unknown" | "customer" {
  if (!(error instanceof ConsoleApiError)) return "none";
  if (error.reason === "uuid_is_customer") return "customer";
  return error.reason === "no_such_staff" ? "unknown" : "none";
}

export function FeaturesScreen() {
  const { client } = useApi();
  const { me } = useStaff();
  const hash = useHash();
  const settingId = selectedId(hash, "features");

  const [surface, setSurface] = useState<FeatureSurface>("app");
  const [fields, setFields] = useState<SubjectFields>(emptySubject);
  // Nothing is fetched until Search: the radios and the box say what to ask
  // for, and pressing the button is what asks. `searched` is the subject the
  // table on screen belongs to, which is not always the one typed.
  const [searched, setSearched] = useState<FeatureSubject | null>(null);
  // Counted, so pressing Search on the same subject asks again. Keyed on the
  // subject alone, a second Search would change nothing while still throwing
  // the draft away — the worst of both.
  const [searches, setSearches] = useState(0);
  const [subjectProblem, setSubjectProblem] = useState<string | null>(null);
  const subject = searched ?? { kind: "allClients" as const };
  const subjectKey =
    searched === null ? null : `${searched.kind === "user" ? `user:${searched.uuid}` : searched.kind}#${searches}`;
  const list = useLoad(searched === null ? null : () => listCustomerFeatures(client, searched), subjectKey);

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [filters, setFilters] = useState<FeatureFilters>(emptyFilters);
  const [reason, setReason] = useState("");
  const alerts = useAlerts();
  const save = useIntentWrite();

  const values = useLoad<ListCustomerFeatureValuesResponse>(
    settingId ? () => listCustomerFeatureValues(client, settingId as UserFeatureId) : null,
    settingId,
  );
  const [editor, setEditor] = useState<Editor | null>(null);
  const [removing, setRemoving] = useState<CustomerFeatureValue | null>(null);
  const roles = me.staffMember.roles.map((role) => role.name);
  const canSearch = me.features.includes(FEATURE_STAFF_FEATURES.search);
  const canView = me.features.includes(FEATURE_STAFF_FEATURES.view);

  // Another subject is another table: a draft against the old rows would save
  // the wrong versions against the wrong person.

  useEffect(() => {
    setEditor(null);
    setRemoving(null);
  }, [settingId]);

  // `useLoad` reports "loading" from the first render, loader or not. Before
  // a search there is nothing in flight, so nothing should say there is.
  const loading = searched !== null && list.state.status === "loading";
  // The controls have moved on from what the table is showing. Saying so is
  // better than a table that quietly belongs to somebody else.
  const stale = searched !== null && !loading && !sameSubject(subjectOf(fields), searched);
  const features = list.state.data?.features ?? [];
  // Filtering only narrows what is shown. A hidden row that was toggled is
  // still in the draft, and still saved: `changed` counts every one of them.
  const shown = useMemo(
    () => visibleFeatures(features, filters, subject, draft, customerFeatureName),
    [features, filters, subject, draft],
  );
  const changed = changedFeatures(features, draft);
  const missing = list.state.status === "failed" ? missingSubject(list.state.error) : "none";
  const unknownUuid = missing !== "none";

  const changeSubject = (next: Partial<SubjectFields>) => {
    setSubjectProblem(null);
    setFields((current) => ({
      ...current,
      ...next,
      ...(next.uuid === undefined ? {} : { uuid: cleanUuid(next.uuid) }),
    }));
  };

  const search = () => {
    // A person needs the whole number: half of one is a typo, not a subject.
    if (fields.kind === "user" && !isCompleteUuid(fields.uuid)) {
      setSubjectProblem(t("features.subject.needsUuid"));
      return;
    }
    setSubjectProblem(null);
    setDraft(EMPTY_DRAFT);
    setReason("");
    setSearched(subjectOf(fields));
    setSearches((count) => count + 1);
  };

  const submit = () => {
    save.edited();
    void save.run(
      saveProblem(reason, features, draft),
      (key) => saveCustomerFeatures(client, saveRequest(subject, reason, features, draft), { idempotencyKey: key }),
      ({ saved }) => {
        setDraft(EMPTY_DRAFT);
        setReason("");
        alerts.ok(countLabel("done", saved));
        list.reload();
      },
    );
  };

  const detail = values.state.data && values.state.data.feature.settingId === settingId ? values.state.data : null;

  return (
    <>
      <PageTop title={t("features.title")} />

      {settingId ? null : (
        <>
          <SurfaceTabs
            surface={surface}
            appCount={features.length}
            consoleCount={STAFF_FEATURE_COUNT}
            onSurface={setSurface}
          />
          {surface === "console" ? <ConsoleSurface /> : null}
          {surface === "console" ? null : (
            <>
              <SubjectBar
                fields={fields}
                resolved={list.state.data?.subject ?? null}
                missing={missing}
                problem={subjectProblem}
                searching={loading}
                onChange={changeSubject}
                onSearch={search}
              />
              {searched === null ? (
                <div className="card">
                  <div className="empty">{t("features.subject.pickFirst")}</div>
                </div>
              ) : null}
              {searched !== null && list.state.status === "failed" && !unknownUuid ? (
                <LoadFailed
                  message={t("features.loadFailed")}
                  retry={t("features.retry")}
                  error={list.state.error}
                  onRetry={list.reload}
                />
              ) : null}
              {!list.state.data && loading ? <Loading text={t("features.loading")} /> : null}
              {list.state.data && !unknownUuid ? (
                <>
                  {stale ? (
                    <div className="banner" role="status">
                      {t("features.subject.stale")}
                    </div>
                  ) : null}
                  <FeatureFilterBar
                    features={features}
                    filters={filters}
                    onChange={(next) => setFilters((current) => ({ ...current, ...next }))}
                  />
                  <FeatureTable
                    features={shown}
                    subject={subject}
                    draft={draft}
                    onToggle={(feature) => {
                      save.edited();
                      setDraft((current) => withToggle(current, feature, subject));
                    }}
                  />
                  {save.error ? (
                    <WriteFailureNotice failure={describeFeatureFailure(save.error)} onReload={list.reload} />
                  ) : null}
                  <SaveBar
                    count={changed.length}
                    reason={reason}
                    pending={save.pending}
                    problem={save.problem}
                    onReason={(next) => {
                      save.edited();
                      setReason(next);
                    }}
                    onDiscard={() => {
                      setDraft(EMPTY_DRAFT);
                      setReason("");
                      save.edited();
                    }}
                    onSave={submit}
                  />
                </>
              ) : null}
            </>
          )}
        </>
      )}

      {settingId ? (
        values.state.status === "failed" && !detail ? (
          <div className="card">
            <LoadFailed
              message={t("features.loadFailed")}
              retry={t("features.retry")}
              error={values.state.error}
              onRetry={values.reload}
            />
          </div>
        ) : !detail ? (
          <div className="card">
            <Loading text={t("features.loading")} />
          </div>
        ) : (
          <FeatureDetail
            feature={detail.feature}
            values={detail.values}
            canViewAccounts={canView}
            lock={featureLock(detail.feature, me.features)}
            roles={roles}
            editingId={editor?.kind === "edit" ? editor.value.settingValueId : null}
            onAdd={() => setEditor({ kind: "add" })}
            onEdit={(value) => setEditor({ kind: "edit", value })}
            onRemove={(value) => setRemoving(value)}
            editor={
              editor ? (
                <ExceptionEditor
                  key={editor.kind === "edit" ? editor.value.settingValueId : "add"}
                  feature={detail.feature}
                  values={detail.values}
                  editing={editor.kind === "edit" ? editor.value : null}
                  canSearch={canSearch}
                  canView={canView}
                  onCancel={() => setEditor(null)}
                  onReload={values.reload}
                  onDone={(message) => {
                    alerts.ok(message);
                    setEditor(null);
                    values.reload();
                  }}
                />
              ) : undefined
            }
          />
        )
      ) : null}

      {removing && detail ? (
        <RemoveDialog
          feature={detail.feature}
          value={removing}
          onClose={() => setRemoving(null)}
          onReload={values.reload}
          onDone={() => {
            alerts.ok(t("features.done.removed"));
            setRemoving(null);
            values.reload();
          }}
        />
      ) : null}
    </>
  );
}

function ExceptionEditor(props: {
  feature: CustomerFeatureListItem;
  values: readonly CustomerFeatureValue[];
  editing: CustomerFeatureValue | null;
  canSearch: boolean;
  canView: boolean;
  onCancel: () => void;
  onReload: () => void;
  onDone: (message: string) => void;
}) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [fields, setFields] = useState<ExceptionFields>(() =>
    props.editing ? fieldsFrom(props.editing) : emptyFields(props.feature),
  );
  const change = (next: Partial<ExceptionFields>) => {
    write.edited();
    setFields((current) => ({ ...current, ...next }));
  };
  const submit = () => {
    const problem = exceptionProblem(props.feature, fields, {
      values: props.values,
      editing: props.editing,
      canSearch: props.canSearch,
      now: new Date(),
    });
    void write.run(
      problem,
      (key) => sendSetException(client, props.feature.settingId, setRequest(fields, props.editing), key),
      () => props.onDone(t(props.editing ? "features.done.saved" : "features.done.added")),
    );
  };
  return (
    <ExceptionFormView
      feature={props.feature}
      editing={props.editing}
      fields={fields}
      onChange={change}
      canSearch={props.canSearch}
      personPicker={
        <PersonPicker
          pro={fields.scope === "proProfile"}
          canView={props.canView}
          picked={fields.person}
          onPick={(person) => change({ person })}
        />
      }
      minExpiry={toLocalInput(new Date())}
      pending={write.pending}
      problem={write.problem}
      failure={write.error ? describeFeatureFailure(write.error) : null}
      onSubmit={submit}
      onCancel={props.onCancel}
      onReload={props.onReload}
    />
  );
}

function RemoveDialog(props: {
  feature: CustomerFeatureListItem;
  value: CustomerFeatureValue;
  onClose: () => void;
  onReload: () => void;
  onDone: () => void;
}) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [reason, setReason] = useState("");
  const failure = write.error ? describeFeatureFailure(write.error) : null;
  return (
    <Modal title={t("features.remove.title")} onClose={props.onClose}>
      <form
        className="modal-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void write.run(
            removeProblem(reason),
            (key) => sendRemoveException(client, props.value, reason, key),
            props.onDone,
          );
        }}
      >
        <div className="modal-body">
          <p>
            {t("features.remove.body", {
              feature: customerFeatureName(props.feature.settingId),
              scope: scopeLabel(props.value.target.scope),
              target: targetLabel(props.value),
              value: valueLabel(props.value.value),
            })}
          </p>
        </div>
        <ReasonField
          value={reason}
          onChange={(next) => {
            write.edited();
            setReason(next);
          }}
        />
        {write.problem ? <p className="field-problem">{write.problem}</p> : null}
        {failure ? <WriteFailureNotice failure={failure} onReload={props.onReload} /> : null}
        <div className="btns modal-actions">
          <button className="btn quiet" type="button" onClick={props.onClose}>
            {t("features.form.cancel")}
          </button>
          <button className="btn danger" type="submit" disabled={write.pending}>
            {write.pending ? t("features.form.sending") : t("features.remove.submit")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
